import React, { useState, useEffect, useCallback, useContext, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import i18n from 'i18n-js';
import { UserContext } from 'context';
import { Table } from 'components/UI';
import Layout from 'components/Layout';
import Hidden from '@material-ui/core/Hidden';
import { useHistory, withRouter } from 'react-router-dom';
import { buildUrl, toQueryString } from 'utils';
import SearchIcon from '@material-ui/icons/Search';
import useStyles from './VehiclesTypesList.styled';
import { truncateKeywordSpaces } from 'utils/utils';
import { SearchField } from 'components/FormFields';
import { vehicleActions } from 'state/modules/vehicles';
import InputAdornment from '@material-ui/core/InputAdornment';
import { Box, Paper, Button, Container, Typography, Grid } from '@material-ui/core';
import { useQuery } from 'hooks';
import { debounce } from 'lodash';

const VehiclesTypesList = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { orgType } = useContext(UserContext);
  const history = useHistory();
  const urlParams = useQuery();
  const isMounted = useRef(false);
  const abortController = useRef(null);
  const [resetPaginationToggle, setResetPaginationToggle] = useState(false);

  const loadingVehiclesTypes = useSelector((state) => state.vehicles.loadingVehiclesTypes);
  const vehiclesTypes = useSelector((state) => state.vehicles.vehiclesTypes);
  const pagination = useSelector((state) => state.vehicles.pagination);

  const [params, setParams] = useState({ page: 1, limit: 10, ...urlParams });

  const columns = [
    {
      name: 'ID',
      selector: 'id',
      sortable: true,
      grow: 2,
    },
    {
      name: i18n.t('vehicle_types.year'),
      selector: i18n.t('vehicle_types.year').toLowerCase(),
      sortable: true,
      grow: 2,
    },
    {
      name: i18n.t('vehicle_types.make'),
      selector: i18n.t('vehicle_types.make').toLowerCase(),
      sortable: true,
      grow: 2,
    },
    {
      name: i18n.t('vehicle_types.model'),
      selector: i18n.t('vehicle_types.model').toLowerCase(),
      sortable: true,
      grow: 2,
    },
    {
      name: i18n.t('vehicle_types.trim'),
      selector: i18n.t('vehicle_types.trim').toLowerCase(),
      grow: 2,
    },
  ];

  const fetchVehiclesTypes = useCallback(
    (params = {}) => {
      // Abort previous if any request
      abortController?.current?.abort();
      // Create new abort controller and send signal
      abortController.current = new AbortController();
      dispatch(
        vehicleActions.fetchVehiclesTypes(params, { signal: abortController?.current?.signal })
      );
    },
    [dispatch]
  );
  const fetchVehiclesTypesDebounced = useCallback(
    debounce((params) => fetchVehiclesTypes(params), 500),
    [fetchVehiclesTypes]
  );

  useEffect(() => {
    dispatch(vehicleActions.resetVehicleTypeOnboarding());
  }, [dispatch]);

  useEffect(() => {
    if (isMounted.current) {
      // On params change once is mounted, debounced
      fetchVehiclesTypesDebounced(params);
    } else {
      // Run once on mount, no debounce
      isMounted.current = true;
      fetchVehiclesTypes(params);
    }
  }, [fetchVehiclesTypes, isMounted, fetchVehiclesTypesDebounced, params]);

  const handleParams = (params) => {
    const data = { ...params };
    setParams(data);
    const _params = toQueryString(data);
    history.replace(buildUrl(`/vehicle-types${_params}`));
  };

  /**
   * Handle page change on table
   * @param {number} page Number of the page
   */
  const handlePageChange = (page) => {
    const newParams = { page: page, limit: params.limit };
    handleParams({ ...params, ...newParams });
  };

  /**
   * Handle sort change on table
   * @param {string} column Column of the table
   * @param {string} sortDirection Sort direction
   */
  const handleSort = ({ column, sortDirection }) => {
    const newParams = {
      ...params,
      page: 1,
      sort_field: column.selector,
      sort_desc: sortDirection === 'desc' ? true : false,
    };
    handleParams(newParams);
    setResetPaginationToggle(!resetPaginationToggle);
  };

  /**
   * Handle per rows (limit) change on table
   * @param {number} limit New limit value
   */
  const handlePerRowsChange = async (limit) => {
    const newParams = { page: 1, limit: limit };
    handleParams({ ...params, ...newParams });
  };

  /**
   * Handle row click
   * @param {object} row Data of the row selected
   */
  const handleRowClick = (row) => {
    history.push(buildUrl(`/vehicle-types/${row?.id}`));
  };

  // search by status, keyword and organization
  const search = (name, value) => {
    const filters = {
      ...params,
      page: 1,
      // validate if value is an object
      [name]: name === 'organization_id' ? value.id : value,
    };
    // remove empty fields
    Object.keys(filters).forEach((key) => !filters[key] && delete filters[key]);
    if (filters?.keyword) {
      filters.keyword = truncateKeywordSpaces(filters.keyword);
    }
    handleParams({ ...filters });
  };

  return (
    <Layout title="Vehicle types">
      <Container className={classes.container}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Grid container spacing={2} direction="row" alignItems="center" justifyContent="space-between">
              <Grid item>
                <Typography variant="h6" gutterBottom>
                  <Box fontWeight="fontWeightBold" data-cy="title" fontSize={24} mb={1}>
                    {i18n.t('vehicle_types.title')}
                  </Box>
                </Typography>
              </Grid>
              {orgType !== 'msServiceOrg' && (
                <Grid item>
                  <Hidden only="xs">
                    <Button
                      data-cy="create-vehicle-type-button"
                      onClick={() => history.push(buildUrl('/vehicle-types/create'))}
                      variant="contained"
                      style={{ textTransform: 'capitalize' }}
                      color="primary"
                    >
                      {i18n.t('vehicle_types.create_new_vehicle_type')}
                    </Button>
                  </Hidden>
                </Grid>
              )}
            </Grid>
          </Grid>
          <Grid item xs={12} style={{ paddingBottom: '25px' }}>
            <Grid container direction="row" justifyContent="flex-start" alignItems="flex-end" spacing={2}>
              <Grid item xs={12} sm={4} md={3}>
                <SearchField
                  label={'Search vehicle type'}
                  field={{
                    name: 'keyword',
                    onChange: search,
                    value: params.keyword,
                  }}
                  inputProps={{
                    startAdornment: (
                      <InputAdornment
                        classes={{
                          root: classes.searchIcon,
                        }}
                        position="start"
                      >
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <Paper elevation={2} className={classes.paper}>
              <Table
                columns={columns}
                page={params.page}
                onSort={handleSort}
                limit={params.limit}
                data={vehiclesTypes}
                loading={loadingVehiclesTypes}
                handleRowClicked={handleRowClick}
                handlePageChange={handlePageChange}
                totalRows={pagination?.totalRows || 0}
                handlePerRowsChange={handlePerRowsChange}
                paginationDefaultPage={pagination?.currentPage || 1}
                paginationResetDefaultPage={resetPaginationToggle}
              />
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Layout>
  );
};

export default VehiclesTypesList;
